import { isClientSide } from "./is-client-side"

const DB_NAME = "mindtrip-store"
// IMPORTANT: bump DB_VERSION whenever STORE_NAMES changes, or existing users
// won't get the new store (onupgradeneeded only fires on a version increase).
const DB_VERSION = 3

// Database previously managed by Dexie. We open under a fresh name (Dexie
// opened "mindtrip-db" at IDB version 10, which would collide here) and
// best-effort delete the old one on init.
const LEGACY_DB_NAME = "mindtrip-db"

export const FLIGHT_SEARCH_BACK_STATE_STORE = "flightSearchBackState"
export const VOICE_TRACE_STORE = "voiceTrace"
export const VOICE_TRACE_CHAT_INDEX = "chatId"
export const VOICE_TRACE_SESSION_INDEX = "chatIdVoiceSessionId"
export const VOICE_TRACE_LEGACY_SESSION_INDEX = "chatIdOpenaiCallId"

const KEYED_STORE_NAMES = [FLIGHT_SEARCH_BACK_STATE_STORE]
const AUTO_INCREMENT_STORE_NAMES = [VOICE_TRACE_STORE]

function idbAvailable(): boolean {
  return isClientSide() && typeof indexedDB !== "undefined"
}

function toError(error: DOMException | null, fallback: string): Error {
  return new Error(error ? `${error.name}: ${error.message}` : fallback)
}

let dbPromise: Promise<IDBDatabase> | null = null
let legacyCleanedUp = false

function deleteDatabase(name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(name)
    request.onsuccess = () => resolve()
    request.onerror = () =>
      reject(toError(request.error, "deleteDatabase failed"))
    // Another open connection is holding the DB; it will be deleted once the
    // connection closes. Don't block the caller waiting for it.
    request.onblocked = () => resolve()
  })
}

function cleanupLegacyDb(): void {
  if (legacyCleanedUp) return
  legacyCleanedUp = true
  void deleteDatabase(LEGACY_DB_NAME).catch(() => {
    // best-effort; orphaned legacy data is harmless
  })
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)
    request.onupgradeneeded = () => {
      const db = request.result
      for (const name of KEYED_STORE_NAMES) {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { keyPath: "key" })
        }
      }
      for (const name of AUTO_INCREMENT_STORE_NAMES) {
        if (!db.objectStoreNames.contains(name)) {
          db.createObjectStore(name, { autoIncrement: true })
        }
      }

      const transaction = request.transaction
      if (!transaction) {
        throw new Error("IndexedDB upgrade transaction unavailable")
      }
      const voiceTraceStore = transaction.objectStore(VOICE_TRACE_STORE)
      if (!voiceTraceStore.indexNames.contains(VOICE_TRACE_CHAT_INDEX)) {
        voiceTraceStore.createIndex(VOICE_TRACE_CHAT_INDEX, "chatId")
      }
      if (!voiceTraceStore.indexNames.contains(VOICE_TRACE_SESSION_INDEX)) {
        voiceTraceStore.createIndex(VOICE_TRACE_SESSION_INDEX, [
          "chatId",
          "voiceSessionId",
        ])
      }
      if (
        !voiceTraceStore.indexNames.contains(VOICE_TRACE_LEGACY_SESSION_INDEX)
      ) {
        voiceTraceStore.createIndex(VOICE_TRACE_LEGACY_SESSION_INDEX, [
          "chatId",
          "openaiCallId",
        ])
      }
    }
    request.onsuccess = () => {
      const db = request.result
      // Let other tabs upgrade/delete the DB: close our handle on a version
      // change and drop the cached promise so the next call reopens. Dexie
      // managed this for us; the raw API does not.
      db.onversionchange = () => {
        db.close()
        dbPromise = null
      }
      db.onclose = () => {
        dbPromise = null
      }
      resolve(db)
    }
    request.onerror = () =>
      reject(toError(request.error, "IndexedDB open failed"))
    request.onblocked = () => reject(new Error("IndexedDB open blocked"))
  })
}

function getDb(): Promise<IDBDatabase> | undefined {
  if (!idbAvailable()) return undefined
  if (!dbPromise) {
    cleanupLegacyDb()
    dbPromise = openDb().catch((error) => {
      console.error("Failed to open database", error)
      // Allow a future call to retry opening.
      dbPromise = null
      throw error
    })
  }
  return dbPromise
}

export async function idbGet<T>(
  storeName: string,
  key: string
): Promise<T | undefined> {
  const promise = getDb()
  if (!promise) return undefined
  const db = await promise
  return new Promise<T | undefined>((resolve, reject) => {
    const request = db
      .transaction(storeName, "readonly")
      .objectStore(storeName)
      .get(key)
    request.onsuccess = () => resolve(request.result ?? undefined)
    request.onerror = () => reject(toError(request.error, "idbGet failed"))
  })
}

export async function idbPut<T>(storeName: string, value: T): Promise<void> {
  const promise = getDb()
  if (!promise) return
  const db = await promise
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite")
    tx.objectStore(storeName).put(value)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(toError(tx.error, "idbPut failed"))
    tx.onabort = () => reject(toError(tx.error, "idbPut aborted"))
  })
}

export async function idbAppend<T>(
  storeName: string,
  value: T
): Promise<number | undefined> {
  const promise = getDb()
  if (!promise) return undefined
  const db = await promise
  return new Promise<number | undefined>((resolve, reject) => {
    const request = db
      .transaction(storeName, "readwrite")
      .objectStore(storeName)
      .add(value)
    request.onsuccess = () =>
      resolve(typeof request.result === "number" ? request.result : undefined)
    request.onerror = () => reject(toError(request.error, "idbAppend failed"))
  })
}

export interface IdbCursorChunk<T> {
  entries: T[]
  continuationKey?: number
}

export async function idbGetNewestIndexChunk<T>(
  storeName: string,
  indexName: string,
  key: IDBValidKey,
  continuationKey: number | undefined,
  limit: number
): Promise<IdbCursorChunk<T>> {
  const promise = getDb()
  if (!promise) return { entries: [] }
  const db = await promise
  return new Promise<IdbCursorChunk<T>>((resolve, reject) => {
    const entries: T[] = []
    let lastKey: number | undefined
    let continuationTarget =
      continuationKey === undefined ? undefined : continuationKey - 1
    const request = db
      .transaction(storeName, "readonly")
      .objectStore(storeName)
      .index(indexName)
      .openCursor(IDBKeyRange.only(key), "prev")

    request.onsuccess = () => {
      const cursor = request.result
      if (!cursor) {
        resolve({ entries })
        return
      }
      if (typeof cursor.primaryKey !== "number") {
        reject(new Error("IndexedDB auto-increment key is not numeric"))
        return
      }
      if (continuationTarget !== undefined) {
        const target = continuationTarget
        continuationTarget = undefined
        cursor.continuePrimaryKey(key, target)
        return
      }

      entries.push(cursor.value)
      if (entries.length > limit) {
        entries.pop()
        resolve({ entries, continuationKey: lastKey })
        return
      }
      lastKey = cursor.primaryKey
      cursor.continue()
    }
    request.onerror = () =>
      reject(toError(request.error, "idbGetNewestIndexChunk failed"))
  })
}

export async function idbGetChatIndexBoundaryEntries<T>(
  storeName: string,
  indexName: string,
  chatId: number
): Promise<T[]> {
  const promise = getDb()
  if (!promise) return []
  const db = await promise
  return new Promise<T[]>((resolve, reject) => {
    const entries: T[] = []
    const index = db
      .transaction(storeName, "readonly")
      .objectStore(storeName)
      .index(indexName)
    const range = IDBKeyRange.bound([chatId], [chatId, "￿"])
    let completedCursors = 0

    const collectBoundaryEntries = (direction: "nextunique" | "prevunique") => {
      const request = index.openCursor(range, direction)
      request.onsuccess = () => {
        const cursor = request.result
        if (!cursor) {
          completedCursors += 1
          if (completedCursors === 2) {
            resolve(entries)
          }
          return
        }
        entries.push(cursor.value)
        cursor.continue()
      }
      request.onerror = () =>
        reject(toError(request.error, "idbGetChatIndexBoundaryEntries failed"))
    }

    collectBoundaryEntries("nextunique")
    collectBoundaryEntries("prevunique")
  })
}

export async function idbCount(storeName: string): Promise<number> {
  const promise = getDb()
  if (!promise) return 0
  const db = await promise
  return new Promise<number>((resolve, reject) => {
    const request = db
      .transaction(storeName, "readonly")
      .objectStore(storeName)
      .count()
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(toError(request.error, "idbCount failed"))
  })
}

export async function idbTrimOldest(
  storeName: string,
  removeCount: number
): Promise<void> {
  if (removeCount <= 0) return
  const promise = getDb()
  if (!promise) return
  const db = await promise
  return new Promise<void>((resolve, reject) => {
    let removed = 0
    const tx = db.transaction(storeName, "readwrite")
    const store = tx.objectStore(storeName)
    const request = store.openCursor()

    request.onsuccess = () => {
      const cursor = request.result
      if (!cursor || removed >= removeCount) return

      cursor.delete()
      removed += 1
      cursor.continue()
    }
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(toError(tx.error, "idbTrimOldest failed"))
    tx.onabort = () => reject(toError(tx.error, "idbTrimOldest aborted"))
  })
}

export async function idbClearStore(storeName: string): Promise<void> {
  const promise = getDb()
  if (!promise) return
  const db = await promise
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite")
    tx.objectStore(storeName).clear()
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(toError(tx.error, "idbClearStore failed"))
    tx.onabort = () => reject(toError(tx.error, "idbClearStore aborted"))
  })
}

export async function idbDelete(storeName: string, key: string): Promise<void> {
  const promise = getDb()
  if (!promise) return
  const db = await promise
  return new Promise<void>((resolve, reject) => {
    const tx = db.transaction(storeName, "readwrite")
    tx.objectStore(storeName).delete(key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(toError(tx.error, "idbDelete failed"))
    tx.onabort = () => reject(toError(tx.error, "idbDelete aborted"))
  })
}

export const destroyDdb = async (): Promise<void> => {
  try {
    const promise = dbPromise
    dbPromise = null
    if (promise) {
      const db = await promise.catch(() => null)
      db?.close()
    }
    if (idbAvailable()) {
      await deleteDatabase(DB_NAME)
    }
  } catch (error) {
    console.error("Failed to destroy database", error)
  }
}
